// Libs
import _map from 'lodash/map'
import _keyBy from 'lodash/keyBy'

// Effect Managers
import { all, call, cancel, cancelled, fork, put, race, select, take } from 'redux-saga/effects'

// fc-spatial
import {
  // Action Types
  UPDATE_SPATIAL,

  // Action Creators
  createSpatial,
  removeSpatial,

  // Decoder
  createSpatialObject,

  // Model Properties
  PROP_GEOJSON,
} from 'fc-spatial'

import {
  PROP_ASSET_ID,
  PROP_SHAPE,
  PROP_SEASON,
} from 'fc-model-assets'

import {
  PROP_SHAPE_ID,
  PROP_SHAPE_DATA,
  PROP_ASSET,
  PROP_FEATURES,
} from 'fc-model-shapes'

import {
  EDIT_SELECTED_FIELD,
} from '../constants'

// Selectors
import featureOperation$ from '../selectors/featureOperation'
import { selectedField$ } from 'hfty-map'

// Controller
import shapesCtrl from 'fc-ctrl-shapes'
import assetsCtrl from 'fc-ctrl-assets'

// Operators
import featureOperator from '../featureOperator'

// Utils
import traverse from 'fc-utils/traverse'

// Turf
// import turfFlatten from '@turf/flatten'

// Local Utils
const getUpdatedSpatialGeoJSON = traverse(['spatial', PROP_GEOJSON])

const getShapeData = traverse([PROP_SHAPE, PROP_SHAPE_DATA], { default: {
  type: 'FeatureCollection',
  features: [],
}})

const getShapeDataFromOperation = traverse(['model', PROP_SHAPE, PROP_SHAPE_DATA])
const getShapeFromOperation = traverse(['model', PROP_SHAPE])
const getFeatures = traverse(['model', PROP_SHAPE, PROP_SHAPE_DATA, PROP_FEATURES])

// Exports
export default function *watchEditSelectedField () {
  yield call(shapesCtrl.start)
  yield fork(shapesCtrl.watch)

  while (true) {
    yield take(EDIT_SELECTED_FIELD)
    yield fork(handleEditSelectedFieldOperation)
  }
}

export function *handleEditSelectedFieldOperation () {
  const field = yield select(selectedField$)
  const shapeData = getShapeData(field)

  const shape = field[PROP_SHAPE] || {
    [PROP_SHAPE_DATA]: shapeData,
    [PROP_ASSET]: field[PROP_ASSET_ID],
  }

  yield put(featureOperator.actionCreators.edit({
    model: {
      ...field,
      [PROP_SHAPE]: shape,
    },
  }))

  const operation = yield select(featureOperation$)

  if (operation) {
    const feature = getShapeDataFromOperation(operation)

    const spatial = createSpatialObject({
      [PROP_GEOJSON]: feature,
    })
    const spatialTask = yield fork(watchUpdateSpatial, { spatial, operation })
    const action1 = createSpatial({spatial})
    yield put(action1)
    yield fork(watchSubmitFeatureOperation, { spatialTask })
  }
}

export function *watchUpdateSpatial ({ spatial, operation }) {
  try {
    while (true) {
      const { payload } = yield take(UPDATE_SPATIAL)
      const featureCollection = getUpdatedSpatialGeoJSON(payload)

      if (featureCollection) {
        const operation = yield select(featureOperation$)

        // As of HEF-1059 New drawing tools - we support multi-polygons so no need to flatten
        // const nextFeatureCollection = turfFlatten(featureCollection)
        featureCollection.features = _map(featureCollection.features, (feature, featureIndex) => ({
          ...feature,
          properties: {
            ...feature.properties,
            featureIndex,
          },
        }))

        const action1 = featureOperator.actionCreators.updateOperation({
          ...operation,
          model: {
            ...operation.model,
            [PROP_SHAPE]: {
              ...operation.model[PROP_SHAPE],
              [PROP_SHAPE_DATA]: {
                ...featureCollection,
                features: _map(featureCollection.features, function (feature, index) {
                  return {
                    ...feature,
                    properties: {
                      ...feature.properties,
                      ...traverse(
                        [PROP_SHAPE, PROP_SHAPE_DATA, 'features', index, 'properties'],
                        { default: {} }
                      )(operation.model),
                    },
                  }
                }),
              },
            },
          },
        })

        yield put(action1)
      }
    }
  } finally {
    if (yield cancelled()) {
      yield put(removeSpatial({spatial}))
    }
  }
}

export function *watchSubmitFeatureOperation ({spatialTask}) {
  let done
  while (!done) {
    const { submit } = yield race({
      submit: take(featureOperator.actionTypes.submitOperation()),
      cancel: take(featureOperator.actionTypes.cancelOperation()),
    })
    const operation = yield select(featureOperation$)

    if (submit) {
      const features = getFeatures(operation)
      const hasFeatures = features.length > 0

      const { shapeResponse, assetResponse } = yield all({
        shapeResponse: (hasFeatures ? call(handleSubmitShape, operation) : {}),
        assetResponse: call(handleSubmitAsset, operation),
      })

      const errors = [
        ...(shapeResponse.error ? shapeResponse.response : []),
        ...(assetResponse.error ? assetResponse.response : []),
      ]

      const action = assetsCtrl.actionCreators.receive({
        data: _keyBy([{
          ...operation.model,
          ...assetResponse.model,
          [PROP_SHAPE]: shapeResponse.model || false,
        }], PROP_ASSET_ID),
      })

      yield put(action)

      done = !errors.length
    } else {
      done = true
    }

    if (done) {
      if (submit) {
        yield put(featureOperator.actionCreators.cancelOperation(operation))
      }

      yield cancel(spatialTask)
    }
  }
}

export function *handleSubmitAsset (operation) {
  return yield call(assetsCtrl.update, {
    model: operation.model,
    queryParams: {
      season: operation.model[PROP_SEASON],
    },
  })
}

export function *handleSubmitShape (operation) {
  const shape = getShapeFromOperation(operation)
  const shapeData = getShapeDataFromOperation(operation)

  if (shapeData.features.length) {
    if (shape[PROP_SHAPE_ID]) {
      return yield call(shapesCtrl.update, {
        model: shape,
      })
    }

    return yield call(shapesCtrl.create, {
      model: shape,
    })
  }

  return yield call(shapesCtrl.delete, {
    id: shape[PROP_SHAPE_ID],
    model: shape,
  })
}
